import { createSelector } from "reselect"
import moment from "moment"
import {
  getCarrotLite,
  getCompanyHasIvfRequirements,
  getCompanyName,
  getIsAvaActive,
  getParentCompanyOrCompanyName
} from "reducers/companyInfo"
import {
  getLifetimeBenefitClaimed,
  getLifetimeMax,
  getPlanStartDate,
  getReimbursementPercent,
  getYearlyBenefitClaimed,
  getYearlyMax
} from "reducers/subsidyStatus"
import {
  getCardState,
  getEligibilityDate,
  getFirstName,
  getHasAnUpcomingOrCompletedExpertChat,
  getHasBankInfoFilledOut,
  getHasMetSignatureRequirement,
  getIsLikelyToDoIvf,
  getIsUsa,
  getLastName,
  getPartnerEmail,
  getPartnerFirstName,
  getPartnerLastName,
  getPlanStarted,
  getMemberHasSuccessfullySyncedInsurance
} from "reducers/userInfo"
import CardStates from "utils/CardStates"
import {
  getCompanyHasCarrotSpecificDeductibleEnabled,
  getCompanyHasExternallyManagedDeductibleEnabled,
  getHasDeductible
} from "./deductibleSelectors"
import { getBenefitConfiguration } from "reducers/benefitConfiguration"

export const getEligibilityDateInFuture = createSelector([getEligibilityDate], (eligibilityDate) => {
  if (eligibilityDate) {
    return eligibilityDate > new moment()
  }
  return false
})

export const getIsNetflixMember = createSelector([getParentCompanyOrCompanyName], (parentCompanyOrCompanyName) => {
  return parentCompanyOrCompanyName.includes("Netflix")
})

export const getIsNBAMember = createSelector([getParentCompanyOrCompanyName], (parentCompanyOrCompanyName) => {
  return parentCompanyOrCompanyName === "NBA"
})

export const getIsWNBAMember = createSelector([getParentCompanyOrCompanyName], (parentCompanyOrCompanyName) => {
  return parentCompanyOrCompanyName === "WNBA"
})

export const getIsAvenuesMember = createSelector([getCompanyName], (companyName) => {
  return companyName === "Avenues USA"
})

export const getIsRomanHealthNYMandateMember = createSelector([getCompanyName], (companyName) => {
  return (
    companyName === "RHV" ||
    companyName === "Roman Health Ventures" ||
    companyName === "RHP" ||
    companyName === "Roman Health Pharmacy"
  )
})

export const getIsTestCompany = createSelector([getParentCompanyOrCompanyName], (parentCompanyOrCompanyName) => {
  return (
    parentCompanyOrCompanyName.startsWith("Test Company") ||
    parentCompanyOrCompanyName.includes("Baby Carrot") ||
    parentCompanyOrCompanyName.includes("Arun's Fruit Company")
  )
})

// currently we have a custom view for demo accounts of Latham and Watkins,
// which are set up in Stage DB only
export const getIsLathamAndWatkinsMember = createSelector(
  [getParentCompanyOrCompanyName],
  (parentCompanyOrCompanyName) => {
    return parentCompanyOrCompanyName.includes("Latham") && parentCompanyOrCompanyName.includes("Watkins")
  }
)

export const getIsCompanyUsingWINFertility = createSelector(
  [getParentCompanyOrCompanyName],
  (parentCompanyOrCompanyName) => {
    return parentCompanyOrCompanyName === "Orrick"
  }
)

export const getHasPendingIvfRequirements = createSelector(
  [getCompanyHasIvfRequirements, getHasMetSignatureRequirement, getHasAnUpcomingOrCompletedExpertChat],
  (companyHasIvfRequirements, hasMetSignatureRequirement, hasAnUpcomingOrCompletedExpertChat) => {
    return companyHasIvfRequirements && (!hasMetSignatureRequirement || !hasAnUpcomingOrCompletedExpertChat)
  }
)

export const getHasPendingIvfRequirementsAndLikelyToDoIvf = createSelector(
  [
    getIsLikelyToDoIvf,
    getCompanyHasIvfRequirements,
    getHasMetSignatureRequirement,
    getHasAnUpcomingOrCompletedExpertChat
  ],
  (isLikelyToDoIvf, companyHasIvfRequirements, hasMetSignatureRequirement, hasAnUpcomingOrCompletedExpertChat) => {
    return (
      isLikelyToDoIvf &&
      companyHasIvfRequirements &&
      (!hasMetSignatureRequirement || !hasAnUpcomingOrCompletedExpertChat)
    )
  }
)

// currently we ignore startDate for carrot lite companies
// therefore we don't support carrot lite companies starting early
export const getBenefitStarted = createSelector(
  [getCarrotLite, getPlanStarted, getEligibilityDateInFuture],
  (carrotLite, planStarted, eligibilityDateInFuture) => {
    if (carrotLite) {
      return !eligibilityDateInFuture
    }
    return planStarted && !eligibilityDateInFuture
  }
)

export const getBenefitStartDate = createSelector(
  [getCarrotLite, getPlanStartDate, getEligibilityDate],
  (carrotLite, planStartDate, eligibilityDate) => {
    if (carrotLite) {
      return eligibilityDate
    }

    if (planStartDate > eligibilityDate) {
      return planStartDate
    }

    return eligibilityDate
  }
)

export const getMemberFullName = createSelector(
  [getFirstName, getLastName],
  (firstName, lastName) => `${firstName} ${lastName}`
)

export const getHasPartnerInfo = createSelector(
  [getPartnerFirstName, getPartnerLastName, getPartnerEmail],
  (partnerFirstName, partnerLastName, partnerEmail) => partnerFirstName || partnerLastName || partnerEmail
)

export const getIsMemberOfCarrotCardCompany = createSelector(
  [getCardState],
  (cardState) => cardState !== CardStates.INELIGIBLE
)

export const getPercentageOfYearlyBenefitClaimed = createSelector(
  [getYearlyBenefitClaimed, getYearlyMax],
  (benefitClaimed, yearlyMax) => (benefitClaimed / yearlyMax) * 100
)

export const getPercentageOfLifetimeBenefitClaimed = createSelector(
  [getLifetimeBenefitClaimed, getLifetimeMax],
  (benefitClaimed, yearlyMax) => (benefitClaimed / yearlyMax) * 100
)

export const getIsBenefitLifetimeOnly = createSelector(
  [getLifetimeMax, getYearlyMax],
  (lifetimeMax, yearlyMax) => lifetimeMax === yearlyMax
)

export const getHasCostShare = createSelector(
  [getReimbursementPercent],
  (reimbursementPercent) => reimbursementPercent && reimbursementPercent !== 100
)

export const getHasVirtualCard = createSelector([getIsUsa], (isUsa) => !isUsa)

export const getHasRequestCardInformationPage = createSelector(
  [getIsNBAMember, getIsWNBAMember, getIsAvenuesMember, getIsRomanHealthNYMandateMember],
  (isNBAMember, isWNBAMember, isAvenuesMember, isRomanHealthNYMandateMember) =>
    isNBAMember || isWNBAMember || isAvenuesMember || isRomanHealthNYMandateMember
)

export const getShouldShowOptionalInsuranceInfoPromo = createSelector(
  [getCompanyHasCarrotSpecificDeductibleEnabled, getHasDeductible, getMemberHasSuccessfullySyncedInsurance],
  (companyHasCarrotSpecificDeductibleEnabled, hasDeductible, memberHasSuccessfullySyncedInsurance) => {
    return !memberHasSuccessfullySyncedInsurance && companyHasCarrotSpecificDeductibleEnabled && hasDeductible
  }
)

export const getShouldFillOutBankInfo = createSelector(
  [getHasBankInfoFilledOut, getBenefitConfiguration],
  (hasBankInfoFilledOut, benefitConfiguration) =>
    !hasBankInfoFilledOut && benefitConfiguration.featureConfig.isBankInfoEnabled
)
